import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Activity,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Clock,
  Shield,
  Plug,
  Lock,
  Database,
  RefreshCw,
  Server,
  CreditCard,
  Users,
  FileCheck,
  Zap,
  Globe,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * System Health View - Comprehensive Test Dashboard
 * 
 * Organized into categories:
 * - Data Integrity (Critical for auditing)
 * - Platform Admin (Super admin operations)
 * - Security (2FA, Webhooks, PII)
 * - Integrations (GYG, Stripe, Widget)
 * - Operations (Roles, Bookings, Services)
 */
const SystemHealthView = ({ platformHealth, fetchPlatformHealth }) => {
  // Test results state - organized by category
  const [testResults, setTestResults] = useState({});
  const [runningTests, setRunningTests] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    dataIntegrity: true,
    platformAdmin: true,
    security: false,
    integrations: false,
    operations: false
  });

  // Define all test suites with their categories
  const testSuites = {
    dataIntegrity: {
      title: 'Data Integrity',
      icon: Database,
      color: 'blue',
      description: 'Verify data accuracy across the platform',
      suites: [
        { 
          id: 'data-integrity', 
          name: 'Data Integrity Tests',
          endpoint: '/health/test-suites/data-integrity',
          description: 'Booking completeness, currency, timezone, revenue accuracy'
        },
        { 
          id: 'cross-service-availability', 
          name: 'Cross-Service Availability',
          endpoint: '/health/test-suites/cross-service-availability',
          description: 'Shared asset conflict detection, capacity aggregation'
        },
        { 
          id: 'financials', 
          name: 'Financials / P&L',
          endpoint: '/health/test-suites/financials',
          description: 'Revenue, expenses, refunds, platform fees, net profit'
        }
      ]
    },
    platformAdmin: {
      title: 'Platform Admin',
      icon: Server,
      color: 'purple',
      description: 'Super admin operations & org management',
      suites: [
        { 
          id: 'platform-admin', 
          name: 'Platform Admin Tests',
          endpoint: '/health/test-suites/platform-admin',
          description: 'Org CRUD, activity logs, test org management'
        }
      ]
    },
    security: {
      title: 'Security',
      icon: Shield,
      color: 'red',
      description: 'Authentication, authorization, data protection',
      suites: [
        { 
          id: '2fa-security', 
          name: 'Two-Factor Authentication',
          endpoint: '/health/test-suites/2fa-security',
          description: '2FA enable/disable, code storage, rate limiting'
        },
        { 
          id: 'webhook-security', 
          name: 'Webhook Security',
          endpoint: '/health/test-suites/webhook-security',
          description: 'Signature verification, replay protection'
        }
      ]
    },
    integrations: {
      title: 'Integrations',
      icon: Plug,
      color: 'green',
      description: 'Third-party service integrations',
      suites: [
        { 
          id: 'widget-checkout', 
          name: 'Widget Checkout',
          endpoint: '/health/test-suites/widget-checkout-fixes',
          description: 'Customer booking flow, payment processing'
        },
        { 
          id: 'widget-js-quality', 
          name: 'Widget JS Quality',
          endpoint: '/health/test-suites/widget-js-quality',
          description: 'JavaScript quality, iOS Safari compatibility'
        }
      ]
    },
    operations: {
      title: 'Operations',
      icon: Activity,
      color: 'amber',
      description: 'Core business operations testing',
      suites: [
        { 
          id: 'roles', 
          name: 'Roles & Permissions',
          endpoint: '/health/test-suites/roles',
          description: 'CRUD operations for roles and permissions'
        },
        { 
          id: 'overbooking-prevention', 
          name: 'Overbooking Prevention',
          endpoint: '/health/test-suites/overbooking-prevention',
          description: 'Capacity limits, concurrent booking protection'
        }
      ]
    }
  };

  // Run a single test suite
  const runTestSuite = async (suiteId, endpoint) => {
    setRunningTests(prev => ({ ...prev, [suiteId]: true }));
    
    try {
      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (response.ok) {
        setTestResults(prev => ({
          ...prev,
          [suiteId]: {
            tests: data.tests || data.results || [],
            summary: data.summary || {},
            timestamp: new Date().toISOString()
          }
        }));
        
        const { passed = 0, failed = 0, warnings = 0 } = data.summary || {};
        if (failed === 0 && warnings === 0) {
          toast.success(`All ${passed} tests passed!`);
        } else if (failed === 0) {
          toast.warning(`${passed} passed, ${warnings} warning(s)`);
        } else {
          toast.error(`${failed} test(s) failed`);
        }
      } else {
        toast.error(`Failed to run ${suiteId} tests`);
      }
    } catch (error) {
      console.error(`Error running ${suiteId} tests:`, error);
      toast.error(`Error running tests: ${error.message}`);
    } finally {
      setRunningTests(prev => ({ ...prev, [suiteId]: false }));
    }
  };

  // Run all tests in a category
  const runCategoryTests = async (categoryId) => {
    const category = testSuites[categoryId];
    if (!category) return;
    
    toast.info(`Running ${category.title} tests...`);
    
    for (const suite of category.suites) {
      await runTestSuite(suite.id, suite.endpoint);
    }
  };

  // Run ALL tests
  const runAllTests = async () => {
    toast.info('Running all health tests...');
    
    for (const [categoryId, category] of Object.entries(testSuites)) {
      for (const suite of category.suites) {
        await runTestSuite(suite.id, suite.endpoint);
      }
    }
    
    toast.success('All tests completed!');
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass':
      case 'passed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'fail':
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  // Get category summary
  const getCategorySummary = (categoryId) => {
    const category = testSuites[categoryId];
    let totalPassed = 0, totalFailed = 0, totalWarnings = 0;
    let hasResults = false;
    
    for (const suite of category.suites) {
      const result = testResults[suite.id];
      if (result?.summary) {
        hasResults = true;
        totalPassed += result.summary.passed || 0;
        totalFailed += result.summary.failed || 0;
        totalWarnings += result.summary.warnings || 0;
      }
    }
    
    if (!hasResults) return null;
    
    return { passed: totalPassed, failed: totalFailed, warnings: totalWarnings };
  };

  // Color variants
  const colorVariants = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700' },
    red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', badge: 'bg-red-100 text-red-700' },
    green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', badge: 'bg-green-100 text-green-700' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' }
  };

  // Check if any tests are running
  const isAnyTestRunning = Object.values(runningTests).some(v => v);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Health Dashboard</h2>
          <p className="text-gray-600 text-sm mt-1">
            Comprehensive test suites for platform integrity and reliability
          </p>
        </div>
        <Button
          onClick={runAllTests}
          disabled={isAnyTestRunning}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
        >
          {isAnyTestRunning ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Run All Tests
            </>
          )}
        </Button>
      </div>

      {/* Test Categories */}
      {Object.entries(testSuites).map(([categoryId, category]) => {
        const Icon = category.icon;
        const colors = colorVariants[category.color];
        const summary = getCategorySummary(categoryId);
        const isExpanded = expandedSections[categoryId];
        
        return (
          <Card key={categoryId} className={`${colors.border} border-2`}>
            <CardHeader 
              className={`${colors.bg} cursor-pointer`}
              onClick={() => toggleSection(categoryId)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                  <div className={`p-2 rounded-lg bg-white shadow-sm`}>
                    <Icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{category.title}</CardTitle>
                    <p className="text-sm text-gray-600 mt-0.5">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {summary && (
                    <div className="flex items-center gap-2">
                      {summary.failed > 0 ? (
                        <Badge className="bg-red-100 text-red-700">
                          {summary.failed} failed
                        </Badge>
                      ) : summary.warnings > 0 ? (
                        <Badge className="bg-yellow-100 text-yellow-700">
                          {summary.warnings} warnings
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">
                          {summary.passed} passed
                        </Badge>
                      )}
                    </div>
                  )}
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      runCategoryTests(categoryId);
                    }}
                    disabled={isAnyTestRunning}
                    variant="outline"
                    size="sm"
                  >
                    {category.suites.some(s => runningTests[s.id]) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Run
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            {isExpanded && (
              <CardContent className="pt-4">
                <div className="space-y-4">
                  {category.suites.map((suite) => {
                    const result = testResults[suite.id];
                    const isRunning = runningTests[suite.id];
                    
                    return (
                      <div key={suite.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{suite.name}</h4>
                            <p className="text-xs text-gray-500">{suite.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {result?.summary && (
                              <span className="text-sm text-gray-600">
                                {result.summary.passed || 0}/{(result.summary.total || result.tests?.length || 0)} passed
                              </span>
                            )}
                            <Button
                              onClick={() => runTestSuite(suite.id, suite.endpoint)}
                              disabled={isAnyTestRunning}
                              variant="outline"
                              size="sm"
                            >
                              {isRunning ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                'Run'
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Test Results */}
                        {result?.tests && result.tests.length > 0 && (
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {result.tests.map((test, idx) => (
                              <div
                                key={idx}
                                className={`flex items-center justify-between p-2 rounded text-sm ${
                                  test.status === 'pass' || test.status === 'passed' 
                                    ? 'bg-green-50' 
                                    : test.status === 'fail' || test.status === 'failed'
                                      ? 'bg-red-50'
                                      : test.status === 'warning'
                                        ? 'bg-yellow-50'
                                        : 'bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {getStatusIcon(test.status)}
                                  <span className="font-medium">{test.name}</span>
                                </div>
                                {test.message && (
                                  <span className="text-xs text-gray-500 max-w-md truncate">
                                    {test.message}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* No results yet */}
                        {!result && !isRunning && (
                          <div className="text-center py-4 text-gray-400 text-sm">
                            Click "Run" to execute tests
                          </div>
                        )}
                        
                        {/* Loading state */}
                        {isRunning && (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                            <span className="ml-2 text-gray-500">Running tests...</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Quick Summary */}
      <Card className="bg-gray-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Total Test Suites:</span>{' '}
              {Object.values(testSuites).reduce((acc, cat) => acc + cat.suites.length, 0)}
            </div>
            <div className="text-xs text-gray-400">
              Last refresh: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealthView;
